import { useState } from "react";
import { Link } from "react-router-dom";
import { FAQS } from "@/mocks/faq";
import { usePageMeta } from "@/hooks/usePageMeta";

const LD_JSON = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export default function Faq() {
  usePageMeta({
    title: "韩漫追更常见问题 FAQ｜榜单规则、评分标准、追更方式",
    description:
      "人气韩漫大全常见问题解答：了解榜单排名规则、评分标准、题材分类与追更方式，帮助你更顺畅地追更心仪的韩漫。",
    path: "/faq",
  });

  const [open, setOpen] = useState<number>(0);

  const tags = Array.from(new Set(FAQS.map((f) => f.tag)));

  return (
    <div className="mx-auto max-w-4xl px-4 py-24 md:px-6 md:py-28">
      <nav className="mb-6 text-xs text-foreground-500" aria-label="面包屑">
        <ol className="flex flex-wrap items-center gap-1.5">
          <li><Link to="/" className="hover:text-primary-600">首页</Link></li>
          <li><i className="ri-arrow-right-s-line"></i></li>
          <li aria-current="page" className="text-foreground-700">常见问题</li>
        </ol>
      </nav>

      <div className="max-w-2xl">
        <p className="font-label text-xs font-semibold uppercase tracking-widest text-secondary-600">FAQ</p>
        <h1 className="mt-1 font-heading text-3xl font-bold text-foreground-950 md:text-4xl">韩漫追更常见问题</h1>
        <p className="mt-3 text-sm leading-relaxed text-foreground-600">
          第一次追韩漫不知道怎么下手？这里整理了读者最常问的问题，帮你快速了解榜单规则、评分标准与追更方式。
        </p>
      </div>

      <div className="mt-8 flex flex-wrap gap-2">
        {tags.map((t) => (
          <span key={t} className="whitespace-nowrap rounded-full bg-secondary-100 px-3.5 py-1.5 text-xs font-medium text-secondary-900">
            {t}
          </span>
        ))}
      </div>

      <div className="mt-6 divide-y divide-background-200 overflow-hidden rounded-xl border border-background-200 bg-background-50">
        {FAQS.map((f, i) => {
          const isOpen = open === i;
          return (
            <div key={f.q}>
              <button
                type="button"
                onClick={() => setOpen(isOpen ? -1 : i)}
                aria-expanded={isOpen}
                aria-controls={`faq-panel-${i}`}
                className="flex w-full items-center justify-between gap-4 px-5 py-5 text-left transition-colors hover:bg-background-100 md:px-6"
              >
                <span className="flex items-center gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-secondary-100 text-secondary-900">
                    <i className="ri-question-line text-sm"></i>
                  </span>
                  <span className="font-heading text-base font-semibold text-foreground-950">{f.q}</span>
                </span>
                <i className={`shrink-0 text-lg text-foreground-400 transition-transform duration-300 ${isOpen ? "rotate-180" : ""} ri-arrow-down-s-line`}></i>
              </button>
              <div id={`faq-panel-${i}`} className={`grid transition-all duration-300 ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                <div className="overflow-hidden">
                  <p className="px-14 pb-5 text-sm leading-relaxed text-foreground-600 md:px-[60px]">{f.a}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-8 rounded-xl border border-background-200 bg-background-100 p-6 text-center">
        <h2 className="font-heading text-lg font-bold text-foreground-950">还有问题想问？</h2>
        <p className="mt-2 text-sm text-foreground-600">欢迎通过关于我们页面的反馈表单告诉我们，我们会认真回复每一封来信。</p>
        <Link to="/about" className="mt-4 inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600">
          联系我们 <i className="ri-arrow-right-line"></i>
        </Link>
      </div>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(LD_JSON) }} />
    </div>
  );
}